import { ListItem } from '../models/ListItem';
import { RMCClientRoomMapping, Room } from '../models/room.model';
import GenericService from './genericService';
import { httpService } from './httpService';

class RoomService extends GenericService<Room, ListItem> {
    constructor() {
        super('/api/Room');
    }

    async getByPropertyId(propertyId: number): Promise<Room[]> {
        return await httpService.get<Room[]>(`/api/Room/GetByPropertyId?Id=${propertyId}`, undefined, {
            unwrap: true,
            normalize: true,
            toast: false,
        });
    }

    // async getRoomByPropertyId(propertyId: number): Promise<Room[]> {
    //     return await httpService.get<Room[]>(`/api/Room/GetRoomByPropertyId/${propertyId}`, undefined, {
    //         unwrap: true,
    //         normalize: true,
    //         toast: false,
    //     });
    // }

    async getRoomsByPropertyId(propertyId: number, roomTypeId: number): Promise<any> {
        return await httpService.get<any>(`/api/Room/GetRoomsByPropertyId/${propertyId}/${roomTypeId}`, undefined, {
            unwrap: false, // <-- ensure unwrap is false
            normalize: true,
            toast: false,
        });
    }

  async getRoomsByPropertyIdForUserDetailed(propertyId: number): Promise<any> {
    return await httpService.get<any>(`/api/Room/GetRoomsByPropertyIdForUserDetailed/${propertyId}`, undefined, {
      // keep the same shape as getRoomsByPropertyId usage in components
      unwrap: false,
      normalize: true,
      toast: false,
    });
  }

    async updateRMCRoom(id: number | string, rmcClientMappingId: number | string, item: Partial<Room>, toast = true): Promise<Room> {
        const result = await httpService.put<Room>(`/api/Room/UpdateRMCRoom/${id}/${rmcClientMappingId}`, item, undefined, {
            unwrap: true,
            normalize: false,
            toast,
        });
        return result as Room;
    }

    async getByIdWithRMCClient(id: number | string, isRMCClient: boolean): Promise<Room> {
        return await httpService.get<Room>(`/api/Room/GetById/${id}/${isRMCClient}`, undefined, {
            unwrap: true,
            normalize: true,
            toast: false,
        });
    }

    async getRoomByIdWithMapping(id: number | string, rmcClientMappingId: number | string): Promise<Room> {
        return await httpService.get<Room>(`/api/Room/GetRoomById/${id}/${rmcClientMappingId}`, undefined, {
            unwrap: true,
            normalize: true,
            toast: false,
        });
    }
    async createClientRoomMappings(proposalId: Number, roomIds: number[]): Promise<boolean> {
        try {
            await httpService.post(
                `/api/Room/CreateClientRoomMappings/${proposalId}`,
                roomIds,
                undefined,
                {
                    unwrap: true,
                    toast: true,
                }
            );
            return true;
        } catch (error) {
            console.error('Failed to create client-room mappings', error);
            return false;
        }
    }
    async createRMCClientPropertyMappings(
        rmcClientMappingId: Number,
        cityId: Number,
        mappings: RMCClientRoomMapping[]
    ): Promise<boolean> {
        try {
            await httpService.post(
                `/api/Room/CreateRMCClientRoomMappings/${rmcClientMappingId}/${cityId}`,
                mappings,
                undefined,
                {
                    unwrap: true,
                    toast: true,
                }
            );
            return true;
        } catch (error) {
            console.error('Failed to create client-room mappings', error);
            return false;
        }
    }
    async getRoomIdsByProposal(proposalId: number | string, onlyRmcClient = false): Promise<number[]> {
        return await httpService.get<number[]>(`/api/Room/GetRoomIds/${proposalId}/${onlyRmcClient}`, undefined, {
            unwrap: true,
            normalize: true,
            toast: false,
        });
    }

    async getRoomDetailsByPropertyAndClient(propertyId: number | string, rmcClientMappingId: number | string): Promise<Room[]> {
        return await httpService.get<Room[]>(`/api/Room/GetRoomDetailsByPropertyAndClient?propertyId=${propertyId}&rmcClientMappingId=${rmcClientMappingId}`, undefined, {
            unwrap: true,
            normalize: true,
            toast: false,
        });
    }
    async createRoom(room: Room): Promise<Room> {
  const formData = new FormData();

  // ✅ Required fields
  formData.append("name", room.name);
  formData.append("propertyId", room.propertyId.toString());
  formData.append("roomTypeId", room.roomTypeId.toString());
  formData.append("area", room.area.toString());
  formData.append("numberOfBedrooms", room.numberOfBedrooms.toString());
  formData.append("numberOfBathrooms", room.numberOfBathrooms.toString());
  formData.append("totalInventory", room.totalInventory.toString());

  // ✅ Optional fields with null checks
  if (room.categoryId) formData.append("categoryId", room.categoryId.toString());
  if (room.description) formData.append("description", room.description);
  if (room.categoryDescription) formData.append("categoryDescription", room.categoryDescription);

  // ✅ Arrays as JSON
  if (room.roomCharges?.length)
    formData.append("roomCharges", JSON.stringify(room.roomCharges));

  if (room.rmcRoomCharges?.length)
    formData.append("rmcRoomCharges", JSON.stringify(room.rmcRoomCharges));

  if (room.roomFacilityMappings?.length)
    formData.append("roomFacilityMappings", JSON.stringify(room.roomFacilityMappings));

  if (room.roomComplementaryMappings?.length)
    formData.append("roomComplementaryMappings", JSON.stringify(room.roomComplementaryMappings));
  if (room.rmcRoomComplementaryMappings?.length)
    formData.append("rmcRoomComplementaryMappings", JSON.stringify(room.rmcRoomComplementaryMappings));

  if (room.rmcRoomFacilityMappings?.length)
    formData.append("rmcRoomFacilityMappings", JSON.stringify(room.rmcRoomFacilityMappings));

  // ✅ Handle image files
  if (room.uploadedFiles && room.uploadedFiles.length > 0) {
    for (let i = 0; i < room.uploadedFiles.length; i++) {
      const file = room.uploadedFiles[i];

      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const chunks = [];
      for (let j = 0; j < uint8Array.length; j++) {
        chunks.push(String.fromCharCode(uint8Array[j]));
      }
      const base64String = btoa(chunks.join(""));

      formData.append(`ImageDtos[${i}].ImageByte`, base64String);
      formData.append(`ImageDtos[${i}].FileName`, file.name);
      formData.append(`ImageDtos[${i}].ContentType`, file.type);
      formData.append(`ImageDtos[${i}].SizeInBytes`, file.size.toString());
      formData.append(`ImageDtos[${i}].IsMainImage`, i === 0 ? "true" : "false");
    }
  }

  // ✅ POST to API
  const result = await httpService.post<Room>(
    `/api/Room/Create`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    },
    {
      unwrap: true,
      normalize: false,
      toast: true
    }
  );

  return result;
}

  async bulkUpdateRmcOverrides(payload: { clientPropertyMappingId: number; rooms: { roomId: number; amount?: number; taxPercentage?: number; complementaryIds?: number[] }[] }): Promise<string> {
    const res = await httpService.post<string>(`/api/Room/BulkUpdateRMCOverrides`, payload, undefined, {
      unwrap: true,
      normalize: false,
      toast: true,
    });
    return res as unknown as string;
  }

  async updateRoom(id: number | string, room: Partial<Room> & { uploadedFiles?: File[]; mainImageId?: number; deleteImageIds?: number[]; newMainUploadIndex?: number; }): Promise<Room> {
    const formData = new FormData();

    // Scalar / required-ish fields (only append if provided to avoid overwriting with blanks)
    const scalarKeys: (keyof Room)[] = [
      'name','propertyId','roomTypeId','area','numberOfBedrooms','numberOfBathrooms','totalInventory','categoryId','description','categoryDescription'
    ];
    scalarKeys.forEach(k => {
      const val = room[k];
      if (val !== undefined && val !== null) formData.append(k, String(val));
    });

  // Ensure Id is sent so backend roomDto.Id matches route id (avoids 400 BadRequest)
  formData.append('id', String(id));

    // JSON collections if provided
    if (room.roomCharges?.length) formData.append('roomCharges', JSON.stringify(room.roomCharges));
    if (room.rmcRoomCharges?.length) formData.append('rmcRoomCharges', JSON.stringify(room.rmcRoomCharges));
    if (room.roomFacilityMappings?.length) formData.append('roomFacilityMappings', JSON.stringify(room.roomFacilityMappings));
    if (room.roomComplementaryMappings?.length) formData.append('roomComplementaryMappings', JSON.stringify(room.roomComplementaryMappings));
    if (room.rmcRoomComplementaryMappings?.length) formData.append('rmcRoomComplementaryMappings', JSON.stringify(room.rmcRoomComplementaryMappings));
    if (room.rmcRoomFacilityMappings?.length) formData.append('rmcRoomFacilityMappings', JSON.stringify(room.rmcRoomFacilityMappings));

    // Image management metadata
    if (room.mainImageId) formData.append('MainImageId', String(room.mainImageId));
    if (room.deleteImageIds?.length) room.deleteImageIds.forEach(idVal => formData.append('DeleteImageIds', String(idVal)));
    if (room.newMainUploadIndex !== undefined && room.newMainUploadIndex !== null) formData.append('NewMainUploadIndex', String(room.newMainUploadIndex));

    // New uploads
    if (room.uploadedFiles?.length) {
      for (let i = 0; i < room.uploadedFiles.length; i++) {
        const file = room.uploadedFiles[i];
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const chunks: string[] = [];
        for (let j = 0; j < uint8Array.length; j++) chunks.push(String.fromCharCode(uint8Array[j]));
        const base64String = btoa(chunks.join(''));
  formData.append(`ImageDtos[${i}].ImageByte`, base64String); // base64 expected server side
        formData.append(`ImageDtos[${i}].FileName`, file.name);
        formData.append(`ImageDtos[${i}].ContentType`, file.type);
        formData.append(`ImageDtos[${i}].SizeInBytes`, file.size.toString());
        // Mark main if either index 0 and no explicit mainImageId, or matches newMainUploadIndex
        const isMain = (room.newMainUploadIndex === i) || (!room.mainImageId && i === 0);
        formData.append(`ImageDtos[${i}].IsMainImage`, isMain ? 'true' : 'false');
      }
    }

  const result = await httpService.put<Room>(`/api/Room/UpdateWithImages/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }, {
      unwrap: true,
      normalize: false,
      toast: true
    });
    return result as Room;
  }


}

export const roomService = new RoomService();
